import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { Dialog } from 'primereact/dialog';
import { ProgressSpinner } from 'primereact/progressspinner';
import moment from 'moment';

import { getJobHrTimelineApi } from '../../redux/apis/jobs';
import { isDarkMode, wordCapitalize } from '../../utils/common';

const ZOOMS = ['year', 'month', 'day'];
const PX_PER_DAY = { day: 54, month: 13, year: 3.6 };
const LANE_H = 90;
const DAY_MS = 86400000;
const MIN_BAR_WIDTH = 80;

/**
 * Parses a 'YYYY-MM-DD' date string into a local Date object at midnight.
 *
 * @param {string} s - Date string in ISO format (YYYY-MM-DD).
 * @returns {Date} Parsed Date object.
 */
const toDate = (s) => new Date(s + 'T00:00:00');

/**
 * Calculates the total number of whole calendar days between two Date objects.
 *
 * @param {Date} a - Starting Date.
 * @param {Date} b - Ending Date.
 * @returns {number} Integer number of days between dates (rounded).
 */
const daysBetween = (a, b) => Math.round((b.getTime() - a.getTime()) / DAY_MS);

/**
 * Returns a new Date object offset by a given number of days.
 *
 * @param {Date} d - Base Date.
 * @param {number} n - Number of days to add (or subtract if negative).
 * @returns {Date} New Date instance with offset applied.
 */
const addDays = (d, n) => new Date(d.getTime() + n * DAY_MS);

// ── Theme tokens ─────────────────────────────────────────────────────────────
const DARK = {
  modalBg: '#2f3349',
  modalBorder: '1px solid rgba(255, 255, 255, 0.1)',
  auroraOp: 0.25,
  headerBorder: '1px solid rgba(255, 255, 255, 0.08)',
  titleSub: '#a5a8c5',
  titleMain: '#ffffff',
  accentIcon: '#7367f0',
  zoomBg: 'rgba(255, 255, 255, 0.06)',
  zoomBorder: '1px solid rgba(255, 255, 255, 0.12)',
  zoomLabel: '#d0d2d6',
  zoomBtnDisabled: '#5d6387',
  closeBtnBg: 'rgba(255, 255, 255, 0.06)',
  closeBtnBorder: '1px solid rgba(255, 255, 255, 0.12)',
  closeBtnColor: '#a5a8c5',
  headerBg: '#25293c',
  tickBorderStrong: 'rgba(255, 255, 255, 0.15)',
  tickBorderWeak: 'rgba(255, 255, 255, 0.06)',
  tickLabelTop: '#a5a8c5',
  tickLabelSub: '#b6bee3',
  tickLabelSubStrong: '#ffffff',
  gridStrong: 'rgba(255, 255, 255, 0.08)',
  gridWeak: 'rgba(255, 255, 255, 0.03)',
  laneEven: 'rgba(255, 255, 255, 0.02)',
  laneOdd: 'rgba(255, 255, 255, 0.008)',
  laneBorder: '1px solid rgba(255, 255, 255, 0.05)',
  todayLine: 'rgba(52, 211, 153, 0.85)',
  todayGlow: '0 0 12px rgba(52, 211, 153, 0.6)',
  barBorder: '1px solid rgba(255, 255, 255, 0.22)',
  barSheen:
    'linear-gradient(100deg, transparent 20%, rgba(255, 255, 255, 0.18) 50%, transparent 80%)',
  barAvatarBg: 'rgba(255, 255, 255, 0.22)',
  barAvatarBorder: '1.5px solid rgba(255, 255, 255, 0.45)',
  barAvatarColor: '#ffffff',
  barText: '#ffffff',
  tooltipBg: '#25293c',
  tooltipBorder: '1px solid rgba(255, 255, 255, 0.12)',
  tooltipCode: '#a5a8c5',
  tooltipTitle: '#ffffff',
  tooltipHr: '#d0d2d6',
  tooltipDate: '#b6bee3',
  tooltipTrack: 'rgba(255, 255, 255, 0.1)',
  emptyColor: '#a5a8c5',
  spinnerColor: undefined
};

const LIGHT = {
  modalBg: 'rgba(248,250,255,0.99)',
  modalBorder: '1px solid rgba(0,0,0,0.1)',
  auroraOp: 0.18,
  headerBorder: '1px solid rgba(0,0,0,0.08)',
  titleSub: '#94a3b8',
  titleMain: '#1e293b',
  accentIcon: '#0284c7',
  zoomBg: 'rgba(0,0,0,0.05)',
  zoomBorder: '1px solid rgba(0,0,0,0.12)',
  zoomLabel: '#334155',
  zoomBtnDisabled: '#c0c9d4',
  closeBtnBg: 'rgba(0,0,0,0.04)',
  closeBtnBorder: '1px solid rgba(0,0,0,0.12)',
  closeBtnColor: '#64748b',
  headerBg: 'rgba(241,245,249,0.97)',
  tickBorderStrong: 'rgba(0,0,0,0.18)',
  tickBorderWeak: 'rgba(0,0,0,0.06)',
  tickLabelTop: '#94a3b8',
  tickLabelSub: '#64748b',
  tickLabelSubStrong: '#1e293b',
  gridStrong: 'rgba(0,0,0,0.07)',
  gridWeak: 'rgba(0,0,0,0.03)',
  laneEven: 'rgba(0,0,0,0.018)',
  laneOdd: 'rgba(0,0,0,0.008)',
  laneBorder: '1px solid rgba(0,0,0,0.06)',
  todayLine: 'rgba(5,150,105,0.85)',
  todayGlow: '0 0 12px rgba(5,150,105,0.4)',
  barBorder: '1px solid rgba(255,255,255,0.3)',
  barSheen: 'linear-gradient(100deg, transparent 20%, rgba(255,255,255,0.28) 50%, transparent 80%)',
  barAvatarBg: 'rgba(255,255,255,0.32)',
  barAvatarBorder: '1.5px solid rgba(255,255,255,0.6)',
  barAvatarColor: '#ffffff',
  barText: '#ffffff',
  tooltipBg: 'rgba(255,255,255,0.97)',
  tooltipBorder: '1px solid rgba(0,0,0,0.1)',
  tooltipCode: '#94a3b8',
  tooltipTitle: '#1e293b',
  tooltipHr: '#334155',
  tooltipDate: '#64748b',
  tooltipTrack: 'rgba(0,0,0,0.08)',
  emptyColor: '#94a3b8',
  spinnerColor: undefined
};

const HUE_COLORS = {
  1: 'linear-gradient(100deg, rgba(2,132,199,0.95) 0%, rgba(124,58,237,0.85) 100%)',
  2: 'linear-gradient(100deg, rgba(5,150,105,0.95) 0%, rgba(2,132,199,0.85) 100%)',
  3: 'linear-gradient(100deg, rgba(217,119,6,0.95) 0%, rgba(5,150,105,0.85) 100%)',
  4: 'linear-gradient(100deg, rgba(225,29,72,0.95) 0%, rgba(217,119,6,0.85) 100%)',
  5: 'linear-gradient(100deg, rgba(192,38,211,0.95) 0%, rgba(225,29,72,0.85) 100%)',
  6: 'linear-gradient(100deg, rgba(124,58,237,0.95) 0%, rgba(192,38,211,0.85) 100%)'
};

/**
 * Extracts up to two uppercase initial letters from a full name string.
 *
 * @param {string} name - Full name of the user/HR.
 * @returns {string} Two-letter uppercase initials (e.g., 'JD'), or 'HR' fallback.
 */
const initials = (name) =>
  name
    ? name
        .split(' ')
        .map((p) => p[0])
        .slice(0, 2)
        .join('')
        .toUpperCase()
    : 'HR';

/**
 * Greedy Interval Scheduling / Lane Packing algorithm.
 * Arranges overlapping time ranges into non-overlapping horizontal lanes
 * to ensure visual clarity without collision.
 *
 * @param {Array<Object>} items - Array of assignment objects containing `start` and `end` ISO date strings.
 * @returns {{ placed: Array<Object>, laneCount: number }} Array of items with assigned `lane` index, and total number of lanes.
 */
function packLanes(items) {
  const sorted = [...items].sort((x, y) => toDate(x.start).getTime() - toDate(y.start).getTime());
  const laneEnds = [];
  const placed = [];
  for (const item of sorted) {
    const s = toDate(item.start).getTime();
    const e = toDate(item.end).getTime();
    let lane = laneEnds.findIndex((end) => end < s);
    if (lane === -1) lane = laneEnds.length;
    laneEnds[lane] = e;
    placed.push({ ...item, lane });
  }
  return { placed, laneCount: Math.max(1, laneEnds.length) };
}

/**
 * Generates timeline scale ticks/headers and grid line coordinates based on active zoom level.
 *
 * @param {Date} start - Leftmost date boundary of the timeline.
 * @param {number} totalDays - Total duration in calendar days displayed across the timeline width.
 * @param {('day'|'month'|'year')} zoom - Active zoom granularity.
 * @param {number} ppd - Pixels allocated per day.
 * @returns {Array<Object>} Array of tick metadata objects (key, left position, width, header labels, style flags).
 */
function buildTicks(start, totalDays, zoom, ppd) {
  const ticks = [];
  if (!start || totalDays <= 0) return ticks;

  if (zoom === 'day') {
    for (let i = 0; i < totalDays; i++) {
      const d = addDays(start, i);
      const dow = d.getDay();
      ticks.push({
        key: `d${i}`,
        left: i * ppd,
        width: ppd,
        top: d.toLocaleDateString('en-US', { weekday: 'short' }).slice(0, 2),
        sub: String(d.getDate()),
        strong: dow === 1
      });
    }
  } else if (zoom === 'month') {
    let i = 0;
    let safety = 0;
    while (i < totalDays && safety++ < 5000) {
      const d = addDays(start, i);
      const monthEnd = new Date(d.getFullYear(), d.getMonth() + 1, 1);
      const span = Math.max(1, Math.min(daysBetween(d, monthEnd), totalDays - i));
      ticks.push({
        key: `m${i}`,
        left: i * ppd,
        width: span * ppd,
        top: d.toLocaleDateString('en-US', { month: 'short' }),
        sub: String(d.getFullYear()),
        strong: d.getMonth() % 3 === 0
      });
      i += span;
    }
  } else {
    let i = 0;
    let safety = 0;
    while (i < totalDays && safety++ < 5000) {
      const d = addDays(start, i);
      const q = Math.floor(d.getMonth() / 3);
      const qEnd = new Date(d.getFullYear(), q * 3 + 3, 1);
      const span = Math.max(1, Math.min(daysBetween(d, qEnd), totalDays - i));
      ticks.push({
        key: `q${i}`,
        left: i * ppd,
        width: span * ppd,
        top: `Q${q + 1}`,
        sub: String(d.getFullYear()),
        strong: q === 0
      });
      i += span;
    }
  }
  return ticks;
}

// Smart tooltip — portal-rendered, never causes layout shift or scrollbars
const TOOLTIP_W = 252;
const TOOLTIP_H_EST = 180;

const HoverTooltip = ({ hover, t }) => {
  const ref = useRef(null);
  const [pos, setPos] = useState({ left: -9999, top: -9999 });

  // Recalculate bounded tooltip position whenever cursor coordinates change
  useEffect(() => {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const tw = TOOLTIP_W;
    const th = ref.current ? ref.current.offsetHeight : TOOLTIP_H_EST;
    const OFFSET = 14;
    let left = hover.x + OFFSET;
    let top = hover.y + OFFSET;
    // Boundary check: Flip to left if overflowing right edge
    if (left + tw > vw - 8) left = hover.x - tw - OFFSET;
    if (left < 8) left = 8;
    // Boundary check: Flip to top if overflowing bottom edge
    if (top + th > vh - 8) top = hover.y - th - OFFSET;
    if (top < 8) top = 8;
    setPos({ left, top });
  }, [hover.x, hover.y]);

  return (
    <div
      ref={ref}
      style={{
        position: 'fixed',
        left: pos.left,
        top: pos.top,
        width: TOOLTIP_W,
        zIndex: 2147483647,
        pointerEvents: 'none',
        background: t.tooltipBg,
        border: t.tooltipBorder,
        backdropFilter: 'blur(24px)',
        WebkitBackdropFilter: 'blur(24px)',
        borderRadius: '14px',
        padding: '14px 16px',
        boxShadow: '0 20px 50px rgba(0,0,0,0.35)',
        color: t.tooltipTitle
      }}>
      <p
        style={{ fontSize: '0.9rem', fontWeight: 700, color: t.tooltipTitle, margin: '0 0 8px 0' }}>
        {hover.a.job}
      </p>
      <p style={{ fontSize: '0.8rem', color: t.tooltipHr, margin: '0 0 2px 0' }}>
        <strong>{hover.a.hr}</strong> ·{' '}
        <span style={{ color: hover.a.isActive ? '#059669' : '#e11d48', fontWeight: 600 }}>
          {hover.a.role}
        </span>
      </p>
      {hover.a.email && (
        <p style={{ fontSize: '0.75rem', color: t.accentIcon, margin: '0 0 8px 0' }}>
          {hover.a.email}
        </p>
      )}
      <p style={{ fontSize: '0.75rem', color: t.tooltipDate, margin: '0 0 10px 0' }}>
        {toDate(hover.a.start).toLocaleDateString('en-US', {
          day: '2-digit',
          month: 'short',
          year: 'numeric'
        })}{' '}
        –{' '}
        {toDate(hover.a.end).toLocaleDateString('en-US', {
          day: '2-digit',
          month: 'short',
          year: 'numeric'
        })}
      </p>
      <div
        style={{
          height: '5px',
          borderRadius: '9999px',
          background: t.tooltipTrack,
          overflow: 'hidden'
        }}>
        <div
          style={{
            height: '100%',
            width: '100%',
            borderRadius: '9999px',
            background: HUE_COLORS[hover.a.hue]
          }}
        />
      </div>
    </div>
  );
};

const JobHrTimelineModal = ({ visible, onHide, job }) => {
  // Resolve theme once per render — automatically adapts to dark/light mode
  const theme = isDarkMode() ? DARK : LIGHT;

  const [rawAssignments, setRawAssignments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [zoom, setZoom] = useState('month');
  const [scrollLeft, setScrollLeft] = useState(0);
  const [hover, setHover] = useState(null);
  const [mounted, setMounted] = useState(false);

  const scrollerRef = useRef(null);
  const anchor = useRef(0);

  // Fetch timeline allocation data from API whenever modal becomes visible for the given job
  useEffect(() => {
    if (visible && job?.ID) {
      anchor.current = 0;
      setLoading(true);
      setRawAssignments([]);
      getJobHrTimelineApi(job.ID)
        .then((res) => {
          if (res?.status === 200) {
            setRawAssignments(res?.data?.data || []);
          }
        })
        .finally(() => setLoading(false));
    }
  }, [visible, job?.ID]);

  // Handle entry animation mounting state
  useEffect(() => {
    if (visible) {
      setTimeout(() => setMounted(true), 100);
    } else {
      setMounted(false);
    }
  }, [visible]);

  // Format and normalize raw API assignment records into structured timeline items
  const assignments = useMemo(() => {
    if (!rawAssignments || rawAssignments.length === 0) return [];
    return rawAssignments.map((a, idx) => {
      const hrObj = a.AssignedHR;
      const hrName = hrObj
        ? `${wordCapitalize(hrObj.FirstName || '')} ${wordCapitalize(hrObj.LastName || '')}`
        : 'HR User';
      const startDateStr = a.StartDate
        ? moment(a.StartDate).format('YYYY-MM-DD')
        : moment().subtract(14, 'days').format('YYYY-MM-DD');
      const isActive = !!(a.IsActive || !a.EndDate);
      // For active HRs, end date defaults to today
      const endDateStr = a.EndDate
        ? moment(a.EndDate).format('YYYY-MM-DD')
        : moment().format('YYYY-MM-DD');
      const createdDateStr = job?.DateAdded || job?.CreatedAt || startDateStr;

      return {
        id: a.ID || `a_${idx}`,
        job: job?.JobTitle || 'Job Position',
        jobCode: job?.JobCode || `JOB-${(job?.ID || '').slice(0, 6)}`,
        hr: hrName.trim() || 'HR User',
        email: hrObj?.Email || '',
        role: isActive ? 'Active HR' : 'Relieved HR',
        isActive,
        start: startDateStr,
        end: endDateStr,
        createdAt: moment(createdDateStr).format('YYYY-MM-DD'),
        hue: (idx % 6) + 1
      };
    });
  }, [rawAssignments, job]);

  // Pixels per day scale based on active zoom mode
  const ppd = PX_PER_DAY[zoom];

  // Calculate timeline start/end boundaries, total days, and lane distribution
  const { placed, laneCount, start, totalDays } = useMemo(() => {
    if (assignments.length === 0) {
      return {
        placed: [],
        laneCount: 1,
        start: addDays(new Date(), -30),
        totalDays: 90
      };
    }

    const { placed, laneCount } = packLanes(assignments);
    const minMs = Math.min(...assignments.map((a) => toDate(a.start).getTime()));
    const maxMs = Math.max(...assignments.map((a) => toDate(a.end).getTime()));
    const start = addDays(new Date(minMs), -30);
    const endPad = addDays(new Date(maxMs), 30);
    const totalDays = Math.max(90, daysBetween(start, endPad));

    return { placed, laneCount, start, totalDays };
  }, [assignments]);

  const totalWidth = totalDays * ppd;

  // Memoized grid ticks / scale markers
  const ticks = useMemo(
    () => buildTicks(start, totalDays, zoom, ppd),
    [start, totalDays, zoom, ppd]
  );

  const changeZoom = (dir) => {
    const el = scrollerRef.current;
    if (el) anchor.current = (el.scrollLeft + el.clientWidth / 2) / (totalDays * ppd);
    setZoom((z) => {
      const idx = ZOOMS.indexOf(z);
      return ZOOMS[Math.max(0, Math.min(ZOOMS.length - 1, idx + dir))] ?? z;
    });
  };

  const todayOffset = daysBetween(start, new Date());

  // Center viewport on current time (Today) when timeline opens or data loads
  useLayoutEffect(() => {
    if (!visible || loading || totalDays <= 0) return;

    if (!anchor.current) {
      anchor.current = Math.max(0, Math.min(1, todayOffset / totalDays));
    }

    const el = scrollerRef.current;
    if (el) {
      const targetLeft = Math.max(0, anchor.current * totalDays * ppd - el.clientWidth / 2);
      el.scrollLeft = targetLeft;
      setScrollLeft(targetLeft);
    }
  }, [visible, loading, ppd, totalDays, start, todayOffset]);

  // ── Momentum smooth scroll ───────────────────────────────────────────────
  const velocityRef = useRef(0); // Current pixel/frame velocity
  const rafRef = useRef(null); // Animation frame handle

  /**
   * Inertia animation loop for smooth deceleration physics during scrolling.
   * Decays velocity by friction multiplier (~0.82) each animation frame until negligible.
   */
  const startMomentum = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);

    const step = () => {
      const el = scrollerRef.current;
      if (!el) return;

      velocityRef.current *= 0.82; // Friction / decay factor

      if (Math.abs(velocityRef.current) < 0.3) {
        velocityRef.current = 0;
        return;
      }

      el.scrollLeft += velocityRef.current;
      setScrollLeft(el.scrollLeft);
      rafRef.current = requestAnimationFrame(step);
    };

    rafRef.current = requestAnimationFrame(step);
  }, []);

  // Wheel and touchpad gesture handler for pinch-to-zoom and momentum-based horizontal scrolling
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    let zoomAcc = 0;

    const onWheel = (e) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        // Zoom gesture (pinch or Ctrl+Wheel)
        zoomAcc += e.deltaY;
        if (Math.abs(zoomAcc) > 120) {
          changeZoom(zoomAcc < 0 ? 1 : -1);
          zoomAcc = 0;
        }
        return;
      }

      // Check if user intends horizontal scrolling (Shift key or horizontal gesture)
      const isHorizontalScroll = e.shiftKey || Math.abs(e.deltaX) > Math.abs(e.deltaY);

      if (isHorizontalScroll) {
        e.preventDefault();
        const delta = Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY;
        velocityRef.current += delta * 0.25; // Sensitivity multiplier
        velocityRef.current = Math.max(-25, Math.min(25, velocityRef.current));
        startMomentum();
      } else {
        // Vertical wheel: if vertical overflow exists, allow standard vertical scrolling
        if (el && el.scrollHeight > el.clientHeight) {
          return;
        }

        // If no vertical overflow, translate vertical wheel movement to horizontal timeline scrolling
        e.preventDefault();
        velocityRef.current += e.deltaY * 0.25;
        velocityRef.current = Math.max(-25, Math.min(25, velocityRef.current));
        startMomentum();
      }
    };

    el.addEventListener('wheel', onWheel, { passive: false });
    return () => {
      el.removeEventListener('wheel', onWheel);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [ppd, totalDays, zoom, startMomentum]);

  /**
   * Tracks container scrollLeft updates to dynamically position sliding labels within visible view.
   */
  const onScroll = useCallback(() => {
    const el = scrollerRef.current;
    if (el) setScrollLeft(el.scrollLeft);
  }, []);

  return (
    <Dialog
      visible={visible}
      onHide={onHide}
      className="gantt-timeline-modal"
      style={{ width: '960px', maxWidth: '96vw' }}
      header={null}
      closable={false}
      resizable={false}
      draggable={false}>
      <div
        style={{
          position: 'relative',
          overflow: 'hidden',
          borderRadius: '1.25rem',
          background: theme.modalBg,
          border: theme.modalBorder,
          boxShadow: '0 32px 80px rgba(0,0,0,0.5)'
        }}>
        {/* Aurora background */}
        <div
          aria-hidden
          style={{
            position: 'absolute',
            inset: '-40%',
            pointerEvents: 'none',
            background:
              'radial-gradient(38% 44% at 20% 30%, rgba(2,132,199,0.3) 0%, transparent 70%), radial-gradient(34% 40% at 78% 22%, rgba(192,38,211,0.25) 0%, transparent 70%), radial-gradient(40% 46% at 60% 82%, rgba(5,150,105,0.22) 0%, transparent 70%)',
            filter: 'blur(30px)',
            opacity: theme.auroraOp
          }}
        />

        {/* Header */}
        <div
          style={{
            position: 'relative',
            zIndex: 20,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '14px 20px',
            borderBottom: theme.headerBorder
          }}>
          <div>
            <p
              style={{
                fontSize: '0.6rem',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.22em',
                color: theme.titleSub,
                margin: '0 0 4px 0'
              }}>
              Hiring Pipeline
            </p>
            <h2
              style={{
                fontSize: '1.15rem',
                fontWeight: 700,
                color: theme.titleMain,
                margin: 0,
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
              <i
                className="pi pi-chart-bar"
                style={{ color: theme.accentIcon, fontSize: '1rem' }}
              />
              Recruiter Allocation Timeline
              {job?.JobTitle ? ` • ${job.JobTitle}` : ''}
            </h2>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            {/* Zoom pill */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                background: theme.zoomBg,
                border: theme.zoomBorder,
                borderRadius: '9999px',
                padding: '4px 4px'
              }}>
              <button
                type="button"
                onClick={() => changeZoom(-1)}
                disabled={zoom === 'year'}
                style={{
                  width: '30px',
                  height: '30px',
                  border: 'none',
                  borderRadius: '50%',
                  background: 'transparent',
                  color: zoom === 'year' ? theme.zoomBtnDisabled : theme.zoomLabel,
                  cursor: zoom === 'year' ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1rem',
                  lineHeight: 1
                }}>
                −
              </button>
              <span
                style={{
                  width: '58px',
                  textAlign: 'center',
                  fontSize: '0.72rem',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  color: theme.zoomLabel,
                  letterSpacing: '0.06em'
                }}>
                {zoom}
              </span>
              <button
                type="button"
                onClick={() => changeZoom(1)}
                disabled={zoom === 'day'}
                style={{
                  width: '30px',
                  height: '30px',
                  border: 'none',
                  borderRadius: '50%',
                  background: 'transparent',
                  color: zoom === 'day' ? theme.zoomBtnDisabled : theme.zoomLabel,
                  cursor: zoom === 'day' ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1rem',
                  lineHeight: 1
                }}>
                +
              </button>
            </div>

            <button
              type="button"
              onClick={onHide}
              style={{
                width: '32px',
                height: '32px',
                border: theme.closeBtnBorder,
                borderRadius: '50%',
                background: theme.closeBtnBg,
                color: theme.closeBtnColor,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.85rem'
              }}>
              ✕
            </button>
          </div>
        </div>

        {/* Body */}
        {loading ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '60px 40px'
            }}>
            <ProgressSpinner style={{ width: '48px', height: '48px' }} />
          </div>
        ) : placed.length === 0 ? (
          <div
            style={{
              textAlign: 'center',
              padding: '60px 40px',
              color: theme.emptyColor
            }}>
            <i
              className="pi pi-calendar-times"
              style={{ fontSize: '2.5rem', display: 'block', marginBottom: '12px' }}
            />
            <p style={{ margin: 0, fontSize: '0.9rem' }}>
              No HR assignments found for this job position.
            </p>
          </div>
        ) : (
          <div
            ref={scrollerRef}
            onScroll={onScroll}
            className="gantt-scroller"
            style={{
              position: 'relative',
              overflowX: 'auto',
              overflowY: 'auto',
              maxHeight: 'min(520px, calc(75vh - 120px))'
            }}>
            {/* Custom styled scrollbars: Vertical enabled, Horizontal strictly hidden across all browsers */}
            <style>{`
              .gantt-scroller {
                -ms-overflow-style: none;
                scrollbar-width: none;
              }
              .gantt-scroller::-webkit-scrollbar {
                width: 6px;
                height: 0px !important;
              }
              .gantt-scroller::-webkit-scrollbar:horizontal {
                display: none !important;
                height: 0px !important;
              }
              .gantt-scroller::-webkit-scrollbar-thumb:horizontal {
                display: none !important;
              }
              .gantt-scroller::-webkit-scrollbar:vertical {
                width: 6px;
              }
              .gantt-scroller::-webkit-scrollbar-track:vertical {
                background: ${isDarkMode() ? 'rgba(255, 255, 255, 0.04)' : 'rgba(0, 0, 0, 0.04)'};
                border-radius: 4px;
              }
              .gantt-scroller::-webkit-scrollbar-thumb:vertical {
                background: ${isDarkMode() ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)'};
                border-radius: 4px;
              }
              .gantt-scroller::-webkit-scrollbar-thumb:vertical:hover {
                background: ${isDarkMode() ? 'rgba(255, 255, 255, 0.35)' : 'rgba(0, 0, 0, 0.35)'};
              }
            `}</style>

            <div style={{ width: totalWidth, position: 'relative', minWidth: '100%' }}>
              {/* Sticky date header */}
              <div
                style={{
                  position: 'sticky',
                  top: 0,
                  zIndex: 20,
                  height: '52px',
                  display: 'flex',
                  background: theme.headerBg,
                  borderBottom: theme.headerBorder,
                  backdropFilter: 'blur(16px)'
                }}>
                {ticks.map((tick) => (
                  <div
                    key={tick.key}
                    style={{
                      position: 'absolute',
                      left: tick.left,
                      width: tick.width,
                      top: 0,
                      height: '52px',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '1px',
                      borderLeft: `1px solid ${
                        tick.strong ? theme.tickBorderStrong : theme.tickBorderWeak
                      }`
                    }}>
                    <span
                      style={{
                        fontSize: '0.6rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.1em',
                        color: theme.tickLabelTop,
                        fontWeight: 600
                      }}>
                      {tick.top}
                    </span>
                    <span
                      style={{
                        fontSize: '0.8rem',
                        fontWeight: 700,
                        color: tick.strong ? theme.tickLabelSubStrong : theme.tickLabelSub
                      }}>
                      {tick.sub}
                    </span>
                  </div>
                ))}
              </div>

              {/* Rows area */}
              <div
                style={{
                  position: 'relative',
                  height: laneCount * LANE_H + 24,
                  paddingTop: '0'
                }}>
                {/* Grid vertical lines */}
                {ticks.map((tick) => (
                  <div
                    key={`g${tick.key}`}
                    style={{
                      position: 'absolute',
                      left: tick.left,
                      top: 0,
                      bottom: 0,
                      width: '1px',
                      background: tick.strong ? theme.gridStrong : theme.gridWeak
                    }}
                  />
                ))}

                {/* Horizontal lane stripes */}
                {Array.from({ length: laneCount }).map((_, li) => (
                  <div
                    key={`lane${li}`}
                    style={{
                      position: 'absolute',
                      left: 0,
                      right: 0,
                      top: li * LANE_H,
                      height: LANE_H,
                      background: li % 2 === 0 ? theme.laneEven : theme.laneOdd,
                      borderBottom: theme.laneBorder
                    }}
                  />
                ))}

                {/* Today vertical line */}
                {todayOffset > 0 && todayOffset < totalDays && (
                  <div
                    style={{
                      position: 'absolute',
                      left: todayOffset * ppd,
                      top: 0,
                      bottom: 0,
                      width: '2px',
                      background: theme.todayLine,
                      boxShadow: theme.todayGlow,
                      zIndex: 12
                    }}>
                    <div
                      style={{
                        position: 'absolute',
                        top: '-5px',
                        left: '-4px',
                        width: '10px',
                        height: '10px',
                        borderRadius: '50%',
                        background: '#34d399',
                        boxShadow: '0 0 8px #34d399'
                      }}
                    />
                  </div>
                )}

                {/* HR Assignment Bars */}
                {(() => {
                  const todayRight = daysBetween(start, new Date()) * ppd + 20;
                  const activeRights = placed
                    .filter((a) => a.isActive)
                    .map((a) => daysBetween(start, toDate(a.start)) * ppd + MIN_BAR_WIDTH);
                  const maxActiveRight = Math.max(todayRight, ...activeRights, 0);

                  return placed.map((a, i) => {
                    const rawLeft = daysBetween(start, toDate(a.start)) * ppd;
                    let barW;
                    if (a.isActive) {
                      const targetRight = Math.max(rawLeft + MIN_BAR_WIDTH, maxActiveRight);
                      barW = targetRight - rawLeft;
                    } else {
                      const rawW = daysBetween(toDate(a.start), toDate(a.end)) * ppd;
                      barW = Math.max(rawW, MIN_BAR_WIDTH);
                    }

                    const barLeft = rawLeft;
                    const barTop = a.lane * LANE_H + 18;

                    // Slide label into view as we scroll
                    const slide = Math.max(
                      0,
                      Math.min(scrollLeft - barLeft + 8, barW - MIN_BAR_WIDTH)
                    );

                    return (
                      <div
                        key={a.id}
                        style={{
                          position: 'absolute',
                          left: barLeft,
                          top: barTop,
                          width: barW,
                          height: '54px',
                          borderRadius: '9999px',
                          background: HUE_COLORS[a.hue],
                          border: theme.barBorder,
                          boxShadow: a.isActive
                            ? `0 8px 28px -10px rgba(0,0,0,0.5), 0 0 16px -2px ${
                                [
                                  'rgba(2,132,199,0.6)',
                                  'rgba(5,150,105,0.6)',
                                  'rgba(217,119,6,0.6)',
                                  'rgba(225,29,72,0.6)',
                                  'rgba(192,38,211,0.6)',
                                  'rgba(124,58,237,0.6)'
                                ][a.hue - 1]
                              }`
                            : '0 8px 28px -10px rgba(0,0,0,0.5)',
                          cursor: 'pointer',
                          overflow: 'hidden',
                          zIndex: 5,
                          transition: `opacity 0.4s ease ${
                            i * 40
                          }ms, transform 0.4s cubic-bezier(0.16,1,0.3,1) ${i * 40}ms`,
                          opacity: mounted ? 1 : 0,
                          transform: mounted ? 'none' : 'translateY(12px)'
                        }}
                        onMouseMove={(e) => setHover({ a, x: e.clientX, y: e.clientY })}
                        onMouseLeave={() => setHover(null)}>
                        {/* Sheen overlay */}
                        <div
                          style={{
                            position: 'absolute',
                            inset: 0,
                            background: theme.barSheen,
                            pointerEvents: 'none'
                          }}
                        />
                        {/* Label */}
                        <div
                          style={{
                            position: 'absolute',
                            top: 0,
                            bottom: 0,
                            left: 0,
                            right: 0,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            paddingLeft: `${slide + 8}px`,
                            paddingRight: '12px',
                            transition: 'padding-left 0.15s ease-out'
                          }}>
                          <div
                            style={{
                              position: 'relative',
                              width: '36px',
                              height: '36px',
                              flexShrink: 0
                            }}>
                            {/* Pulsing ring for active HRs */}
                            {a.isActive && (
                              <div
                                style={{
                                  position: 'absolute',
                                  inset: '-4px',
                                  borderRadius: '50%',
                                  border: '2px solid rgba(255,255,255,0.7)',
                                  animation: 'activePulse 2s ease-in-out infinite'
                                }}
                              />
                            )}
                            <div
                              style={{
                                width: '36px',
                                height: '36px',
                                borderRadius: '50%',
                                background: theme.barAvatarBg,
                                border: theme.barAvatarBorder,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '0.72rem',
                                fontWeight: 700,
                                color: theme.barAvatarColor,
                                position: 'relative'
                              }}>
                              {initials(a.hr)}
                            </div>
                          </div>
                          <span
                            style={{
                              fontSize: '0.82rem',
                              fontWeight: 600,
                              color: theme.barText,
                              whiteSpace: 'nowrap',
                              textShadow: '0 1px 4px rgba(0,0,0,0.5)',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}>
                            {a.hr}
                          </span>
                          {/* Live dot badge */}
                          {a.isActive && (
                            <span
                              style={{
                                width: '8px',
                                height: '8px',
                                borderRadius: '50%',
                                background: '#34d399',
                                flexShrink: 0,
                                boxShadow: '0 0 6px #34d399',
                                animation: 'activePulse 1.8s ease-in-out infinite'
                              }}
                            />
                          )}
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            </div>
          </div>
        )}

        {/* Hover tooltip rendered via portal so it never affects Dialog layout */}
        {hover && ReactDOM.createPortal(<HoverTooltip hover={hover} t={theme} />, document.body)}
      </div>
    </Dialog>
  );
};

export default JobHrTimelineModal;
